import React, { useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";

function CampaignCard({ data, noGutter, setModal, onView }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const inDateRange = () => {
    const currentDate = new Date();
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const normalizedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const normalizedEndDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return currentDateOnly >= normalizedStartDate && currentDateOnly <= normalizedEndDate;
  };

  const getStatusColor = () => {
    switch (data?.status) {
      case "active":
        return inDateRange() ? "success" : "warning";
      case "paused":
        return "warning";
      case "completed":
        return "secondary";
      case "draft":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusLabel = () => {
    if (data?.status === "active" && !inDateRange()) {
      return "Out of date";
    }
    return data?.status?.charAt(0).toUpperCase() + data?.status?.slice(1);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    setModal({ ...data, type: "delete" });
  };

  const handleDuplicate = () => {
    handleMenuClose();
    setModal({ ...data, type: "duplicate" });
  };

  const formatBudget = (budget) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(budget);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <MDBox
      component="li"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgColor={darkMode ? "transparent" : "grey-100"}
      borderRadius="lg"
      p={2}
      mb={noGutter ? 0 : 1}
      mt={1}
    >
      <MDBox display="flex" flexDirection="column" flex={1}>
        <MDBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <MDTypography variant="button" fontWeight="medium" textTransform="capitalize">
            {data?.name}
          </MDTypography>
          <Chip
            label={getStatusLabel()}
            size="small"
            color={getStatusColor()}
            sx={{
              height: "20px",
              fontSize: "0.65rem",
              fontWeight: 600,
            }}
          />
        </MDBox>

        <MDBox display="flex" gap={2} mt={0.5} flexWrap="wrap">

          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Icon sx={{ fontSize: "14px", color: "text.secondary" }}>calendar_today</Icon>
            <MDTypography variant="caption" color="text">
              {formatDate(data?.startDate)} - {formatDate(data?.endDate)}
            </MDTypography>
          </MDBox>

          {data?.country && (
            <>

              <MDBox display="flex" alignItems="center" gap={0.5}>
                <Icon sx={{ fontSize: "14px", color: "text.secondary" }}>public</Icon>
                <MDTypography sx={{ display: "flex", gap: "5px" }} variant="caption" color="text">
                  {data?.country?.map((el) => (
                    <MDTypography variant="caption" color="text">• {el?.label}</MDTypography>
                  ))}
                </MDTypography>
              </MDBox>
            </>
          )}
        </MDBox>

        {data?.description && (
          <MDTypography variant="caption" color="text" mt={0.5} sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          }}>
            {data?.description}
          </MDTypography>
        )}
      </MDBox>

      <MDBox display="flex" alignItems="center" gap={1}>
        {onView && (
          <MDButton
            onClick={() => onView(data)}
            variant="text"
            color="info"
            size="small"
          >
            <Icon>visibility</Icon>&nbsp;View
          </MDButton>
        )}

        <MDButton
          onClick={() => navigate(`/campaigns/edit/${data?.id}`)}
          variant="text"
          color={darkMode ? "white" : "dark"}
          size="small"
        >
          <Icon>edit</Icon>&nbsp;Edit
        </MDButton>


        <MDButton
          onClick={handleMenuOpen}
          variant="text"
          color={darkMode ? "white" : "dark"}
          size="medium"
          iconOnly
        >
          <Icon>more_vert</Icon>
        </MDButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <Icon sx={{ mr: 1 }}>delete</Icon>
            Delete
          </MenuItem>
        </Menu>
      </MDBox>
    </MDBox>
  );
}

CampaignCard.defaultProps = {
  noGutter: false,
  onView: null,
};

CampaignCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    status: PropTypes.oneOf(["draft", "active", "paused", "completed"]).isRequired,
    budget: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
  noGutter: PropTypes.bool,
  setModal: PropTypes.func.isRequired,
  onView: PropTypes.func,
};

export default CampaignCard;
